import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ContinueButton from "./ContinueButton";
import { useForm } from "react-hook-form";
import Alert from "@mui/material/Alert";

const Driver = ({
  submitting,
  goToBackStep,
  answers,
  setAnswers,
  handleUpdate,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser"));

  const questions = [
    {
      code: "DRIVEREXP",
      title: t("speed.howExperienced"),
      options: [
        { value: "0-3 months", label: t("speed.zeroto3") },
        { value: "3-6 months", label: t("speed.threeto6") },
        { value: "6-12 months", label: t("speed.sixto12") },
        { value: "1+ year", label: t("speed.oneyearPlus") },
      ],
    },
    {
      code: "WHYISA",
      title: t("speed.whyInterested"),
      options: [
        { value: "Develop good habits", label: t("speed.developHabits") },
        { value: "Peace of mind", label: t("speed.peaceOfMind") },
        { value: "Ticket/crash", label: t("speed.ticketAccident") },
        { value: "Other", label: t("speed.other") },
      ],
    },
    {
      code: "HOWSOONISA",
      title: t("speed.howSoon"),
      options: [
        { value: "As soon as possible", label: t("speed.asSoon") },
        { value: "Next 30 days", label: t("speed.next30") },
        { value: "30-60 days", label: t("speed.thirtyto60") },
        { value: "Undecided", label: t("speed.undecided") },
      ],
    },
  ];

  const { handleSubmit } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [validationAttempted, setValidationAttempted] = useState(false);

  useEffect(() => {
    if (answers?.length) {
      const normalized = questions.map((q) => {
        const found = answers.find((a) => a.questionCode === q.code);
        return found ? found : { questionCode: q.code, response: "" };
      });
      setAnswers(normalized);
    } else {
      setAnswers(
        questions.map((q) => ({ questionCode: q.code, response: "" })),
      );
    }
  }, []);

  const handleAnswerChange = (questionCode, newValue) => {
    setAnswers((prev) =>
      prev.map((item) =>
        item.questionCode === questionCode
          ? { ...item, response: newValue }
          : item
      ),
    );
  };

  const unansweredQuestionNumbers = questions
    .map((question, index) =>
      answers?.find((answer) => answer.questionCode === question.code)?.response
        ? null
        : index + 1,
    )
    .filter(Boolean);

  const getErrorMessage = () => {
    if (!validationAttempted || unansweredQuestionNumbers.length === 0) {
      return "";
    }

    if (unansweredQuestionNumbers.length === questions.length) {
      return t("speed.allQuestionsIncomplete");
    }

    if (unansweredQuestionNumbers.length === 1) {
      return t("speed.oneQuestionIncomplete", {
        questionNumber: unansweredQuestionNumbers[0],
      });
    }

    return t("speed.twoQuestionsIncomplete", {
      questionNumbers: unansweredQuestionNumbers.join(` ${t("speed.and")} `),
    });
  };

  const onSubmit = () => {
    setValidationAttempted(true);
    if (unansweredQuestionNumbers.length > 0) {
      return;
    }

    const payload = {
      Step: "Driver",
      clientId: enrolleeUser?.clientId || enrolleeUser?.ClientId || 0,
      responses: answers.map(({ questionCode, response }) => ({
        questionCode,
        response,
      })),
    };

    sessionStorage.setItem("enrolleeUser", JSON.stringify({ ...enrolleeUser }));

    handleUpdate("Driver", payload);
  };

  const errorMessage = getErrorMessage();

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        width: isMobile ? "100%" : "40%",
        maxWidth: isMobile ? "none" : "560px",
        margin: "auto",
        marginBottom: "40px",
      }}
    >
      <Box marginBottom="25px" sx={{ padding: isMobile ? "25px" : "10px" }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        {questions.map((q, index) => {
          const currentAnswer =
            answers?.find((a) => a.questionCode === q.code)?.response || "";
          const showRequiredAsterisk =
            validationAttempted &&
            unansweredQuestionNumbers.includes(index + 1);

          return (
            <Box
              key={q.code}
              sx={{
                mb: index === questions.length - 1 ? 0 : 3,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={500}
                color="primary.main"
                sx={{ mb: 0.5 }}
              >
                {`${t("speed.Q")}${index + 1}: `}
                {q.title}
                {showRequiredAsterisk && (
                  <Box
                    component="span"
                    aria-label={t("speed.answerRequired")}
                    sx={{ color: "error.main", ml: 0.5 }}
                  >
                    *
                  </Box>
                )}
              </Typography>
              <RadioGroup
                aria-label={q.title}
                value={currentAnswer}
                onChange={(e) => handleAnswerChange(q.code, e.target.value)}
              >
                {q.options.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio />}
                    label={opt.label}
                    sx={{ ml: 0 }}
                  />
                ))}
              </RadioGroup>
            </Box>
          );
        })}
      </Box>
      <ContinueButton goToBackStep={goToBackStep} submitting={submitting} />
    </form>
  );
};

function mapStateToProps() {
  return {};
}

Driver.propTypes = {
  submitting: PropTypes.bool,
  goToBackStep: PropTypes.func,
  answers: PropTypes.array,
  setAnswers: PropTypes.func,
  handleUpdate: PropTypes.func,
};

export default connect(mapStateToProps)(Driver);
